import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';

import Avatar from '@material-ui/core/Avatar';
import { Badge, Button } from '@material-ui/core';
import CardMedia from '@material-ui/core/CardMedia';
import DeleteIcon from '@material-ui/icons/Delete';
import ChatIcon from '@material-ui/icons/Chat';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as MerchantService from '../../api/merchantService';
import * as MessageService from '../../api/messageService';

import Paper from '../Paper';
import { truncate } from 'lodash';
import Spinner from '../Spinner';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 16px',
    marginBottom: theme.spacing(),
    width: 350,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginTop: -40,
    marginBottom: theme.spacing(),
  },
  stampCardRoot: {
    [theme.breakpoints.up('sm')]: {
      padding: `${theme.spacing()}px ${theme.spacing(2.5)}px 0`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(),
    },
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
  milestonesContainer: {
    marginTop: '5px',
  },
  minSpend: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(),
  },
  subtext: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: '12px',
  },
  subtextPrize: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    lineHeight: '12px',
  },
  minSpendPrize: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(),
    textAlign: 'center',
  },
  noCardText: {
    fontSize: 16,
    fontWeight: 800,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    padding: theme.spacing(5),
  },
  descContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '5px',
  },
  descContainerPrize: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '5px',
    marginBottom: theme.spacing(2),
  },
  descText: {
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
  },
  subtextContainerPrize: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  subtextContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  markdownContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  markdown: {
    marginTop: 10,
    marginBottom: 10,
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
  },
  cardMedia: {
    height: 220,
    width: 350,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    backgroundSize: 'cover',
  },
  deleteCardContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  primaryIcon: {
    color: theme.palette.primary.main,
    width: '30px',
    height: '30px',
  },
  secondaryIcon: {
    width: '30px',
    height: '30px',
  },
  actionButton: {},
  merchantProfile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  createdAtText: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
    color: '#808080',
  },
  bodyText: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
}));

type Props = {
  merchantId: string;
  messageId: string;
  onReadPress: () => void;
  onDeletePress: () => void;
};

export default function MessageCard({
  merchantId,
  messageId,
  onReadPress,
  onDeletePress,
}: Props) {
  const classes = useStyles();
  const [viewMore, setViewMore] = useState(false);
  const [readStatus, setReadStatus] = useState(false);
  const [merchant, setMerchant] = useState<MerchantService.Merchant | null>(
    null
  );
  const [message, setMessage] = useState<MessageService.Message | null>(null);
  const [msg, setMsg] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (merchantId && messageId && isLoading) {
        const resMerchant = await MerchantService.get(merchantId);
        if (resMerchant) {
          setMerchant(resMerchant);
        }

        const resMessage = await MessageService.get(messageId);
        if (resMessage) {
          setMessage(resMessage);
          setMsg(resMessage.message);
        }

        setIsLoading(false);
      }
    };

    if (merchantId && messageId && isLoading) {
      fetch();
    }
  }, [merchantId, messageId, isLoading]);

  return (
    <Grid item xs={12} className={classes.root}>
      <Paper className={classes.cardContainer}>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 200,
            }}
          >
            <Spinner />
          </div>
        ) : (
          <>
            {message && merchant && (
              <>
                <CardMedia
                  className={classes.cardMedia}
                  image={
                    message.image
                      ? message.image.versions.xl
                      : merchant.cover
                      ? merchant.cover.versions.xl
                      : ''
                  }
                />
                <div className={classes.merchantProfile}>
                  <Avatar
                    alt={'logo'}
                    src={merchant.logo ? merchant.logo.versions.xl : ''}
                    className={classes.avatar}
                  />
                  <Typography
                    variant="h5"
                    className={classes.title}
                    gutterBottom
                  >
                    {message.title}
                  </Typography>
                </div>
                <div className={classes.stampCardRoot}>
                  <Typography
                    variant="body2"
                    className={classes.createdAtText}
                    gutterBottom
                  >
                    {moment
                      .unix(message.createdAt)
                      .format('ddd , MMM D , YYYY')}
                  </Typography>
                </div>
                <div className={classes.markdownContainer}>
                  <ReactMarkdown className={classes.markdown}>
                    {msg.length < 220
                      ? msg
                      : !viewMore
                      ? truncate(msg, {
                          length: 220,
                          separator: '',
                        })
                      : msg}
                  </ReactMarkdown>
                </div>
                {/* <Typography
                    variant="body2"
                    className={classes.bodyText}
                    gutterBottom
                  >
                    {message.message.length < 220
                      ? message.message
                      : !viewMore
                      ? truncate(message.message, {
                          length: 220,
                          separator: '',
                        })
                      : message.message}
                  </Typography> */}
                <div className={classes.deleteCardContainer}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => onDeletePress()}
                  >
                    <DeleteIcon className={classes.primaryIcon} />
                  </IconButton>
                  {message.message.length > 220 && (
                    <Button
                      onClick={() => {
                        setViewMore(!viewMore);
                      }}
                    >
                      {!viewMore ? 'More' : 'Hide'}
                    </Button>
                  )}
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => {
                      if (message.status === 'unread') {
                        setReadStatus(!readStatus);
                        onReadPress();
                      }
                    }}
                  >
                    {message.status === 'unread' ? (
                      <Badge overlap="circular" color="primary" variant="dot">
                        <ChatIcon className={classes.primaryIcon} />
                      </Badge>
                    ) : (
                      <ChatIcon className={classes.secondaryIcon} />
                    )}
                  </IconButton>
                </div>
              </>
            )}
          </>
        )}
      </Paper>
    </Grid>
  );
}
